import { useState, useMemo, useCallback } from "react";

import { parseValue } from "../../../../../../ui library/helpers/functions";
import {
  FieldsOrderedList,
  fullWidthFieldsWithNaNTrigger,
  visibilityControlledFields,
} from "../helpers/fieldOrderConfig";
import { getColumnSpan } from "../helpers/functions";
import {
  containerClassesByPropName,
  moduleInputProps,
  moduleProps,
} from "../helpers/propNameHelper";

import { FieldModule } from "./FieldModule";

export function ModuleForm({
  schema,
  data,
  label,
  questionKey,
  handleAnswerChange,
  placeholder,
  subTitle,
  footer,
}) {
  const isFlatSchema = schema?.type && !schema?.expected_input;

  const [form, setForm] = useState(() => (isFlatSchema ? { field: data } : { ...data }));
  const [err, setErr] = useState({});

  const expectedInput = schema?.expected_input;
  const selectedSchema = schema[expectedInput];
  const properties = isFlatSchema ? { field: schema } : selectedSchema?.properties;
  const requiredConditionally = isFlatSchema
    ? { field: schema }
    : selectedSchema?.required_conditionally;

  const fields = Object.keys(properties || {}).filter((name) => name !== "id");

  const orderedFields = useMemo(() => {
    return FieldsOrderedList.filter((key) => fields.includes(key));
  }, [fields]);

  const orderedFieldsModule = useMemo(() => {
    return (orderedFields.length !== 0 ? orderedFields : fields).filter((field) => {
      if (orderedFields.every((key) => visibilityControlledFields.includes(key))) {
        if (form[field] !== undefined) {
          const requiredCondition = requiredConditionally?.[field];

          if (!requiredCondition) {
            return true;
          }

          return (
            Array.isArray(requiredCondition) &&
            requiredCondition.some((condition) => {
              if (Array.isArray(condition)) {
                return condition.some((subCondition) => {
                  const conditionKey = Object.keys(subCondition)[0];
                  const conditionValue = subCondition[conditionKey];

                  if (conditionValue?.oneOf) {
                    return conditionValue.oneOf.includes(form[conditionKey]);
                  }
                  return false;
                });
              } else {
                const conditionKey = Object.keys(condition)[0];
                const conditionValue = condition[conditionKey];

                if (conditionValue?.oneOf) {
                  return conditionValue.oneOf.includes(form[conditionKey]);
                }

                return false;
              }
            })
          );
        }

        return false;
      }

      return true;
    });
  }, [orderedFields, fields, form, requiredConditionally, visibilityControlledFields]);

  const onChange = useCallback(
    (field, value) => {
      const fieldSchema = properties?.[field];
      const type = fieldSchema?.type;
      const parsedValue = parseValue(value, type);
      const updated = { ...form, [field]: parsedValue };
      setForm(updated);
      setErr((e) => ({ ...e, [field]: undefined }));
      const resultToSend = isFlatSchema ? parsedValue : updated;
      handleAnswerChange(questionKey, resultToSend);
    },
    [properties, form, isFlatSchema, handleAnswerChange, questionKey]
  );

  const calculateIsRequired = useCallback(
    (name) => {
      const requiredCondition = requiredConditionally?.[name];
      if (!Array.isArray(requiredCondition)) {
        return false;
      }

      return requiredCondition.some((condition) => {
        if (Array.isArray(condition)) {
          return condition.some((subCondition) => {
            const conditionKey = Object.keys(subCondition)[0];
            const conditionValue = subCondition[conditionKey];

            if (conditionValue?.oneOf) {
              return conditionValue.oneOf.includes(form[conditionKey]);
            }
            return false;
          });
        } else {
          const conditionKey = Object.keys(condition)[0];
          const conditionValue = condition[conditionKey];

          if (conditionValue?.oneOf) {
            return conditionValue.oneOf.includes(form[conditionKey]);
          }

          return false;
        }
      });
    },
    [requiredConditionally, form]
  );

  return (
    <div className="grid grid-cols-1 items-end gap-4 md:grid-cols-12">
      {orderedFieldsModule.map((name, idx) => {
        const prop = { ...properties[name], ...(moduleProps?.[name] ?? {}) };
        const isOnlyOneField = fields?.length === 1;
        const isSingleFieldSchema = isFlatSchema;
        const isNaNTrigger = fullWidthFieldsWithNaNTrigger.includes(name);
        const shouldUseFullWidth = isNaNTrigger && isNaN(form[name]);
        const containerClasses = containerClassesByPropName?.[name] || "";
        const inputProps = moduleInputProps?.[name] ?? {};

        const columnSpan = getColumnSpan({
          isSingleFieldSchema,
          isOnlyOneField,
          shouldUseFullWidth,
          fieldIndex: idx,
        });

        const labelToRender = isSingleFieldSchema || isOnlyOneField ? label : prop?.title || "";
        const isRequired = calculateIsRequired(name);

        return (
          <div key={name} className={`${containerClasses || columnSpan} min-w-0`}>
            <FieldModule
              label={labelToRender}
              isRequired={isRequired}
              name={name}
              prop={prop}
              form={form}
              value={form[name]}
              onChange={onChange}
              enumUrl={typeof prop?.enum === "string" ? prop.enum : undefined}
              placeholder={placeholder}
              subTitle={subTitle}
              footer={footer}
              props={inputProps}
            />
            {err[name] && <p className="mt-1 text-sm text-red-600">{err[name]}</p>}
          </div>
        );
      })}
    </div>
  );
}
